import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import {
    FuseHorizontalNavigationComponent,
    FuseNavigationService,
    FuseVerticalNavigationComponent
} from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UploadfileService } from 'app/services/uploadfile.service';

import { FuseLoadingBarComponent } from '../../../../../@fuse/components/loading-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LanguagesComponent } from '../../../common/languages/languages.component';
import { FuseFullscreenComponent } from '../../../../../@fuse/components/fullscreen';
import { UserComponent } from '../../../common/user/user.component';
import { SettingsfileService } from 'app/layout/common/settings/settings.service';
import { GoogleTranslateService } from '../../../../services/google-translate.service';

@Component({
    selector: 'material-layout',
    templateUrl: './material.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [FuseLoadingBarComponent, MatButtonModule, MatIconModule, RouterOutlet],
    styles: [
        `
        #google_element .goog-te-gadget > span {
            display: none !important;
            background-color: gray !important;
        }
        #google_element .goog-te-gadget {
            font-size: 0 !important;
        }
        #google_element .goog-te-combo {
            font-size: 14px !important;
            font-weight: bold !important;
            background-color: var(--white) !important;
            width: 100% !important;
            max-width: 200px !important;
            margin: 3px 0 !important;
            height: 30px !important;
            padding: 0 5px !important;
            border-radius: 3px !important;
            border: 1px solid black;
            margin-right: 40px !important;
            @media only screen and (max-width: 959px) {
                width: 100% !important;
                max-width: 100% !important;
            }
        }
         .dark {
            #google_element .goog-te-combo {
            color: var(--text-white) !important;
            }
        }
        #google_element {
            @media only screen and (max-width: 959px) {
                position: relative !important;
            }
        }
        #google_element .goog-te-combo:focus {
            outline: none;
            border: 2px solid !important;
        }
        `
    ]
})
export class MaterialLayoutComponent implements OnInit, OnDestroy, AfterViewInit
{
    isScreenSmall: boolean;
    navigation: Navigation;
    customerLogo = 'assets/img/no-img.jpg';
    showGoogleTranslateLabel: boolean = false;
    private _googleTranslateObserver: MutationObserver | null = null;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private uploadFileService: UploadfileService,
        private settingsFileService: SettingsfileService,
        private googleTranslateService: GoogleTranslateService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    ngAfterViewInit() {
        this.googleTranslateService.loadScript().then(() => {
            this.fixGoogleTranslateA11y();
            setTimeout(() => {
                (<any>window).google.translate.TranslateElement({ pageLanguage: 'en' }, 'google_element');
            }, 3000);
            this.setGoogtransCookie();
        }).catch((error) => {
            console.error('Failed to load Google Translate script', error);
        });
        this.settingsFileService.updatedLocalhostValues.subscribe(res => {
            this.customerLogo = ((localStorage.getItem('loginCustomerLogo') !== 'undefined' && localStorage.getItem('loginCustomerLogo') !== undefined && localStorage.getItem('loginCustomerLogo') !== '' && localStorage.getItem('loginCustomerLogo') !== null) ? localStorage.getItem('loginCustomerLogo') : "assets/images/logo/logo.png") || '';
        });
      }

    setGoogtransCookie(): void {
        const browserLanguage = navigator.language.split('-')[0];
        const googtransValue = `/auto/${browserLanguage}`;
        const isHttps = location.protocol === 'https:';
        const secureFlag = isHttps ? '; Secure' : '';
        document.cookie = `googtrans=${googtransValue}; path=/; SameSite=Strict${secureFlag}`;
    }

    private fixGoogleTranslateA11y(): void {
        this._googleTranslateObserver = new MutationObserver(() => {
            const select = document.querySelector('#google_element select');
            if (select) {
                if (!select.getAttribute('aria-label')) {
                    select.setAttribute('aria-label', 'Select language');
                    select.setAttribute('title', 'Select language');
                }
                (select as HTMLElement).id = 'google_translate_select';
                if (!this.showGoogleTranslateLabel) {
                    this.showGoogleTranslateLabel = true;
                    this._changeDetectorRef.detectChanges();
                }
            }
            document.querySelectorAll('#goog-gt-tt').forEach(el => el.remove());
        });
        this._googleTranslateObserver.observe(document.body, { childList: true, subtree: true });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._googleTranslateObserver?.disconnect();
        this._googleTranslateObserver = null;
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }
}
